import { HttpProject, TaskStatus } from "tekkr-common/dist/model/project/project";
import _ from "lodash";

export interface StatusCounts {
    done: number;
    started: number;
    on_hold: number;
    todo: number;
    total: number;
}

export function isAllDone(counts: StatusCounts): boolean {
    return counts.total > 0 && counts.done === counts.total;
}

export function computeStatusCounts(tasks: Array<{ status: TaskStatus | null }>): StatusCounts {
    let done = 0;
    let started = 0;
    let on_hold = 0;
    let todo = 0;
    for (const task of tasks) {
        switch (task.status) {
            case "done":
                done++;
                break;
            case "started":
                started++;
                break;
            case "on_hold":
                on_hold++;
                break;
            default:
                todo++;
                break;
        }
    }
    return { done, started, on_hold, todo, total: tasks.length };
}

export interface ProjectProgress {
    getWorkstreamProgress(workstreamId: string): number;
    getWorkstreamStatusCounts(workstreamId: string): StatusCounts | undefined;
    getDeliverableProgress(workstreamId: string, deliverableId: string): number;
    getDeliverableStatusCounts(workstreamId: string, deliverableId: string): StatusCounts | undefined;
}

export function computeProjectProgress(project: Pick<HttpProject, "tasks">): ProjectProgress {
    const results: Record<string, { statusCounts: Record<string, StatusCounts> }> = {};

    const deliverableGrouped = _.groupBy(project.tasks, (t) =>
        JSON.stringify({ ws: t.workstreamId, d: t.deliverableId })
    );
    const workstreamGrouped = _.groupBy(deliverableGrouped, (g) => g[0].workstreamId);

    for (const wsId in workstreamGrouped) {
        const ws = workstreamGrouped[wsId];
        const statusCounts: Record<string, StatusCounts> = {};

        Object.values(ws).forEach((v) => {
            statusCounts[v[0].deliverableId ?? "workstream"] = computeStatusCounts(v);
        });

        results[wsId] = { statusCounts };
    }

    return {
        getWorkstreamProgress(workstreamId: string): number {
            const ws = results[workstreamId];
            if (!ws) return 0;
            const counts = ws.statusCounts;
            const total = Object.keys(counts).length;
            if (total === 0) return 0;
            const done = Object.values(counts).filter((c) => c.done === c.total).length;
            return done / total;
        },
        getDeliverableProgress(workstreamId: string, deliverableId: string): number {
            const c = results[workstreamId]?.statusCounts[deliverableId];
            if (!c) return 0;
            return c.total > 0 ? c.done / c.total : 0;
        },
        getWorkstreamStatusCounts(workstreamId: string): StatusCounts | undefined {
            const ws = results[workstreamId];
            if (!ws) return undefined;
            const zero: StatusCounts = { done: 0, started: 0, on_hold: 0, todo: 0, total: 0 };
            return Object.values(ws.statusCounts).reduce(
                (acc, c) => ({
                    done: acc.done + c.done,
                    started: acc.started + c.started,
                    on_hold: acc.on_hold + c.on_hold,
                    todo: acc.todo + c.todo,
                    total: acc.total + c.total,
                }),
                zero
            );
        },
        getDeliverableStatusCounts(workstreamId: string, deliverableId: string): StatusCounts | undefined {
            return results[workstreamId]?.statusCounts[deliverableId];
        },
    };
}
